import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Stack,
  alpha,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbsUpDownIcon from "@mui/icons-material/ThumbsUpDown";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RuleIcon from "@mui/icons-material/Rule";
import { SegmentedProgressBar } from "../../../shared/components";
import dayjs from "dayjs";
import { formatCurrency } from "../../../shared/utils";

interface ProjectDetailsCardProps {
  project: {
    id: number;
    title?: string;
    status?: string;
    project_status?: string;
    budget?: number;
    spent?: number;
    remaining?: number;
    start_date?: string;
    end_date?: string;
    progress?: { percentage?: number };
    project_scope?: string;
    contractor?: string;
    address?: string;
    rag?:
      | string
      | {
          time?: string;
          cost?: string;
          quality?: string;
          overall?: string;
        };
    project_reference?: string;
    programme?: string;
    network?: string;
    workstream?: string;
    site?: string;
    phase_template?: string;
    phases?: Array<{
      name?: string;
      status?: string;
      percentage_complete?: number;
    }>;
  } | null;
  isLoading?: boolean;
}

export const ProjectDetailsCard = ({
  project,
  isLoading,
}: ProjectDetailsCardProps) => {
  if (isLoading || !project) {
    return null;
  }

  const completionPercentage = project.progress?.percentage || 0;
  const ragData = typeof project.rag === "object" ? project.rag : null;
  const overallRag =
    ragData?.overall ||
    (typeof project.rag === "string" ? project.rag : "green");

  const getRagColor = (rag?: string): "error" | "warning" | "success" => {
    const ragValue = typeof rag === "string" ? rag : "";
    switch (ragValue.toLowerCase()) {
      case "red":
      case "r":
        return "error";
      case "amber":
      case "a":
        return "warning";
      case "green":
      case "g":
      default:
        return "success";
    }
  };

  const getRagIcon = (rag?: string) => {
    const ragValue = typeof rag === "string" ? rag : "";
    switch (ragValue.toLowerCase()) {
      case "red":
        return <ThumbDownAltIcon sx={{ color: "error.main", fontSize: 20 }} />;
      case "amber":
        return (
          <ThumbsUpDownIcon sx={{ color: "warning.main", fontSize: 20 }} />
        );
      case "green":
      default:
        return <ThumbUpIcon sx={{ color: "success.main", fontSize: 20 }} />;
    }
  };

  const budget = project.budget || 0;
  const spent = project.spent || 0;

  return (
    <Card
      key={project.id}
      variant="outlined"
      sx={{
        width: "100%",
        borderTopWidth: "4px",
        borderTopStyle: "solid",
        borderTopColor:
          getRagColor(overallRag) === "success"
            ? "success.main"
            : getRagColor(overallRag) === "error"
            ? "error.main"
            : "warning.main",
        backgroundColor: (theme) =>
          theme.palette.mode === "dark"
            ? alpha(theme.palette.background.paper, 0.4)
            : alpha(theme.palette.background.paper, 0.8),
        boxShadow: (theme) => theme.shadows[15],
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backdropFilter: "blur(8px)",

        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: "cardTransition 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
        "@keyframes cardTransition": {
          "0%": {
            opacity: 0,
            transform: "translateX(-30px) scale(0.96)",
          },
          "100%": {
            opacity: 1,
            transform: "translateX(0) scale(1)",
          },
        },
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="body1"
            fontWeight="600"
            sx={{
              maxWidth: "70%",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {project.title || "Untitled Project"}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            {getRagIcon(overallRag)}
          </Box>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            mt: 0.5,
          }}
        >
          {project.address || "No location"}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            my: 1,
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {project.workstream || project.programme || "General Project"}
        </Typography>

        {project.contractor && (
          <Chip label={project.contractor} size="small" variant="filled" />
        )}

        <Box display="flex" alignItems="center" gap={1} sx={{ my: 1 }}>
          <SegmentedProgressBar
            phases={(project.phases || []).map((phase, index) => ({
              phase_id: index,
              name: phase.name || "",
              project_id: project.id,
              display_order: index,
              status: phase.status || "",
              percentage_complete: phase.percentage_complete || 0,
            }))}
            progressPercentage={completionPercentage}
            status={
              (overallRag?.toLowerCase() === "red"
                ? "red"
                : overallRag?.toLowerCase() === "amber"
                ? "amber"
                : "green") as "green" | "red" | "amber"
            }
          />
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="caption" color="text.secondary">
            {project.project_reference || "N/A"}
          </Typography>
          <Stack direction="row" spacing={1}>
            <PaymentsIcon
              sx={{
                fontSize: 22,
                color: ragData?.cost
                  ? getRagColor(ragData.cost) === "success"
                    ? "success.main"
                    : getRagColor(ragData.cost) === "error"
                    ? "error.main"
                    : "warning.main"
                  : "text.secondary",
              }}
            />
            <AccessTimeIcon
              sx={{
                fontSize: 22,
                color: ragData?.time
                  ? getRagColor(ragData.time) === "success"
                    ? "success.main"
                    : getRagColor(ragData.time) === "error"
                    ? "error.main"
                    : "warning.main"
                  : "text.secondary",
              }}
            />
            <RuleIcon
              sx={{
                fontSize: 22,
                color: ragData?.quality
                  ? getRagColor(ragData.quality) === "success"
                    ? "success.main"
                    : getRagColor(ragData.quality) === "error"
                    ? "error.main"
                    : "warning.main"
                  : "text.secondary",
              }}
            />
          </Stack>
        </Stack>

        <Box
          sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Card
              variant="outlined"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                width: "100%",
                p: 2,
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Budget
              </Typography>
              <Typography variant="body2" fontWeight="600">
                {formatCurrency(budget)}
              </Typography>
            </Card>
            <Card
              variant="outlined"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                p: 2,
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Spent
              </Typography>
              <Typography variant="body2" fontWeight="600" color="warning.main">
                {formatCurrency(spent)}
              </Typography>
            </Card>
            <Card
              variant="outlined"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                p: 2,
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Remaining
              </Typography>
              <Typography
                variant="body2"
                fontWeight="600"
                color={budget - spent < 0 ? "error.main" : "success.main"}
              >
                {formatCurrency(budget - spent)}
              </Typography>
            </Card>
          </Stack>

          {(project.start_date || project.end_date) && (
            <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
              <Box display="flex" flexDirection="row" gap={0.5}>
                <Typography variant="caption">
                  {project.start_date
                    ? dayjs(project.start_date).format("MMM DD, YYYY")
                    : "N/A"}
                </Typography>
                <Typography variant="caption"> -</Typography>
                <Typography variant="caption">
                  {project.end_date
                    ? dayjs(project.end_date).format("MMM DD, YYYY")
                    : "N/A"}
                </Typography>
                <Typography variant="caption"> - </Typography>
                <Typography variant="caption">
                  {project.end_date
                    ? dayjs(project.end_date).diff(
                        dayjs(project.start_date),
                        "days"
                      ) + " days"
                    : "N/A"}
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
